import { useCallback, useEffect, useState } from 'react';

/**
 * Control density: `counter` (default, a desk terminal) or `bench` (a tablet
 * used standing, with bigger touch targets).
 *
 * Applies `data-density` to <html>, which swaps the --control-h / --row-h /
 * --nav-h variables in styles/appearance.css (Tailwind: h-ctl, h-ctl-sm,
 * h-ctl-lg, min-h-row).
 *
 * Stored per DEVICE, not per user: density is a property of the hardware the
 * terminal is, not of who is signed in to it.
 *
 * AppearanceProvider mounts this once and exposes `density` / `setDensity` on
 * the appearance context — read it from there. Calling this hook again
 * elsewhere would create a second, independent copy of the state.
 *
 * `?density=bench` (or `counter`) overrides and persists, like `?appearance=`.
 */

export const DENSITIES = ['counter', 'bench'];
export const DEFAULT_DENSITY = 'counter';
export const DENSITY_KEY = 'twigs_density';

const isValid = (value) => DENSITIES.includes(value);

const readQueryOverride = () => {
  try {
    const value = new URLSearchParams(window.location.search).get('density');
    return isValid(value) ? value : null;
  } catch {
    return null;
  }
};

const readStored = () => {
  try {
    const value = localStorage.getItem(DENSITY_KEY);
    if (isValid(value)) return value;
  } catch {
    // Private-mode / disabled storage — fall through to the default.
  }
  return DEFAULT_DENSITY;
};

const persist = (value) => {
  try {
    localStorage.setItem(DENSITY_KEY, value);
  } catch {
    // Non-persistent is still better than not switching at all.
  }
};

export default function useDensity() {
  const [density, setDensityState] = useState(() => {
    const override = readQueryOverride();
    if (override) {
      persist(override);
      return override;
    }
    return readStored();
  });

  const setDensity = useCallback((next) => {
    if (!isValid(next)) return;
    setDensityState(next);
    persist(next);
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('data-density', density);
  }, [density]);

  return { density, setDensity, densities: DENSITIES };
}
